import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Accordion, Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";

function eventVariety(event) {
  if (event.variety_formatted) return event.variety_formatted;
  if (event.variety === 1) return "presented";
  if (event.variety === 2) return "answered";
  if (event.variety === 3) return "left";
  if (event.variety === 4) return "revisited";
  if (event.variety === 5) return "answer_changed";
  return "";
}

function eventSummary(events) {
  const list = events || [];
  const presented = list.filter((event) => eventVariety(event) === "presented");
  const changed = list.filter((event) => eventVariety(event) === "answer_changed");
  const firstPresented = presented[0];
  const close = list.find((event) => {
    const variety = eventVariety(event);
    return variety === "answered" || variety === "answer_changed";
  });

  let dwellSeconds = null;
  let dwellExcluded = false;
  if (firstPresented?.occurred_at && close?.occurred_at) {
    const started = new Date(firstPresented.occurred_at).getTime();
    const ended = new Date(close.occurred_at).getTime();
    if (Number.isFinite(started) && Number.isFinite(ended) && ended >= started) {
      dwellSeconds = Math.round((ended - started) / 1000);
      dwellExcluded = Boolean(close.excluded_from_metrics);
    }
  }

  return {
    presentedCount: presented.length,
    changedCount: changed.length,
    dwellSeconds,
    dwellExcluded,
  };
}

function formatOccurredAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function ResponseEventBlock({ row, t }) {
  const [open, setOpen] = useState(false);
  const events = row.events || [];
  if (events.length === 0) return null;

  const summary = eventSummary(events);
  const parts = [];
  if (summary.presentedCount > 0) {
    parts.push(
      t("assessment_event_presentations", { count: summary.presentedCount })
    );
  }
  if (summary.changedCount > 0) {
    parts.push(t("assessment_event_changes", { count: summary.changedCount }));
  }
  if (summary.dwellSeconds != null) {
    parts.push(
      t(
        summary.dwellExcluded
          ? "assessment_event_dwell_excluded"
          : "assessment_event_dwell",
        { seconds: summary.dwellSeconds }
      )
    );
  }

  return (
    <Stack gap={4}>
      {parts.length > 0 && (
        <Text size="xs" c="dimmed">
          {parts.join(" · ")}
        </Text>
      )}
      <Button
        size="compact-xs"
        variant="subtle"
        onClick={() => setOpen((current) => !current)}
      >
        {open ? t("assessment_events_hide") : t("assessment_events_show")}
      </Button>
      {open && (
        <Stack gap={2}>
          {events.map((event) => (
            <Text key={event.id} size="xs" c="dimmed">
              {eventVariety(event)}
              {event.occurred_at ? ` · ${formatOccurredAt(event.occurred_at)}` : ""}
              {event.select_label ? ` · ${event.select_label}` : ""}
              {event.excluded_from_metrics
                ? ` · ${t("assessment_event_excluded")}`
                : ""}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function QuestionRow({ row, t }) {
  return (
    <Stack
      gap={4}
      py={6}
      style={{
        borderBottom: "1px solid var(--mantine-color-default-border)",
      }}
    >
      <Text size="sm">{row.interrogative}</Text>
      <Text size="xs" c="dimmed">
        {row.select_label || t("assessment_response_unanswered")}
      </Text>
      <ResponseEventBlock row={row} t={t} />
    </Stack>
  );
}

function uniqueRowCount(rows) {
  const seen = new Set();
  rows.forEach((row) => seen.add(row.instrument_item_id));
  return seen.size;
}

function textMatches(value, needle) {
  if (!needle) return true;
  return String(value || "")
    .toLowerCase()
    .includes(needle);
}

function questionMatches(row, needle) {
  if (!needle) return true;
  return [row.interrogative, row.legacy_question_code, row.select_label].some(
    (value) => textMatches(value, needle)
  );
}

function pruneResponseTree(tree, needle, unmappedLabel) {
  if (!needle) return tree;

  const roots = tree.roots
    .map((root) => {
      const rootHit =
        textMatches(root.label, needle) || textMatches(root.concept, needle);
      const conceptualItems = root.conceptualItems
        .map((item) => {
          const itemHit =
            textMatches(item.label, needle) || textMatches(item.facet, needle);
          const rows =
            rootHit || itemHit
              ? item.rows
              : item.rows.filter((row) => questionMatches(row, needle));
          if (rows.length === 0) return null;
          return { ...item, rows };
        })
        .filter(Boolean);
      if (conceptualItems.length === 0) return null;
      return {
        ...root,
        conceptualItems,
        questionCount: uniqueRowCount(
          conceptualItems.flatMap((item) => item.rows)
        ),
      };
    })
    .filter(Boolean);

  const unmapped = tree.unmapped.filter(
    (row) =>
      questionMatches(row, needle) || textMatches(unmappedLabel, needle)
  );

  return { roots, unmapped };
}

function nestResponseRows(rows) {
  const roots = new Map();
  const unmapped = [];

  rows.forEach((row) => {
    const links = row.concepts || [];
    if (links.length === 0) {
      unmapped.push(row);
      return;
    }

    links.forEach((link) => {
      const concept = link.concept || "_";
      if (!roots.has(concept)) {
        roots.set(concept, {
          concept,
          label: link.label || concept,
          position: Number(row.position) || 0,
          items: new Map(),
        });
      }
      const root = roots.get(concept);
      const itemKey =
        link.conceptual_item_id || link.facet || link.conceptual_item_label || "_";
      if (!root.items.has(itemKey)) {
        root.items.set(itemKey, {
          id: String(itemKey),
          label: link.conceptual_item_label || link.facet || itemKey,
          facet: link.facet,
          position: Number(row.position) || 0,
          rows: [],
        });
      }
      const conceptualItem = root.items.get(itemKey);
      if (
        !conceptualItem.rows.some(
          (existing) => existing.instrument_item_id === row.instrument_item_id
        )
      ) {
        conceptualItem.rows.push(row);
      }
    });
  });

  const nestedRoots = Array.from(roots.values())
    .map((root) => {
      const conceptualItems = Array.from(root.items.values())
        .map((item) => ({
          ...item,
          rows: item.rows.slice().sort((a, b) => (a.position || 0) - (b.position || 0)),
        }))
        .sort((a, b) => (a.position || 0) - (b.position || 0) || a.label.localeCompare(b.label));
      return {
        concept: root.concept,
        label: root.label,
        position: root.position,
        conceptualItems,
        questionCount: uniqueRowCount(conceptualItems.flatMap((item) => item.rows)),
      };
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.label.localeCompare(b.label));

  return { roots: nestedRoots, unmapped };
}

function GroupControl({ label, countLabel }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm" pr="xs">
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {countLabel && (
        <Text size="xs" c="dimmed">
          {countLabel}
        </Text>
      )}
    </Group>
  );
}

export default function AssessmentResponseLookup({
  assessment,
  filterConcept,
  onClearFilter,
  hideHeading = false,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [openRoots, setOpenRoots] = useState([]);
  const [openItems, setOpenItems] = useState([]);

  const activityStatus = Number(assessment?.activity_status);

  useEffect(() => {
    if (!assessment?.id) return;
    if (!Number.isFinite(activityStatus) || activityStatus < 2) return;

    axios
      .get(`/assessments/${assessment.id}/responses/`)
      .then(({ data }) => {
        const payload = data.response[0] || {};
        setRows(payload.data || []);
      })
      .catch(() => setRows([]));
  }, [assessment?.id, activityStatus]);

  const filterLabel = useMemo(() => {
    if (!filterConcept) return null;
    for (const row of rows) {
      const match = (row.concepts || []).find(
        (entry) => entry.concept === filterConcept
      );
      if (match) return match.label || filterConcept;
    }
    return filterConcept;
  }, [filterConcept, rows]);

  const needle = query.trim().toLowerCase();
  const scopedRows = useMemo(() => {
    if (!filterConcept) return rows;
    return rows.filter((row) =>
      (row.concepts || []).some((entry) => entry.concept === filterConcept)
    );
  }, [rows, filterConcept]);

  const tree = useMemo(
    () =>
      pruneResponseTree(
        nestResponseRows(scopedRows),
        needle,
        t("assessment_response_unmapped")
      ),
    [scopedRows, needle, t]
  );

  useEffect(() => {
    if (needle) {
      const rootKeys = tree.roots.map((root) => root.concept);
      if (tree.unmapped.length > 0) rootKeys.push("unmapped");
      setOpenRoots(rootKeys);
      setOpenItems(
        tree.roots.flatMap((root) =>
          root.conceptualItems.map((item) => item.id)
        )
      );
      return;
    }
    setOpenItems([]);
    if (filterConcept) {
      setOpenRoots([filterConcept]);
      return;
    }
    setOpenRoots([]);
  }, [needle, filterConcept, tree]);

  if (!Number.isFinite(activityStatus) || activityStatus < 2) return null;

  const hasTree = tree.roots.length > 0 || tree.unmapped.length > 0;

  return (
    <Stack gap="sm">
      {(filterConcept || !hideHeading) && (
        <Group justify="space-between" wrap="wrap">
          {!hideHeading && (
            <Text size="sm" fw={600}>
              {t("assessment_responses")}
            </Text>
          )}
          {filterConcept && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {t("assessment_response_filter", { label: filterLabel })}
              </Text>
              <Button size="compact-xs" variant="subtle" onClick={onClearFilter}>
                {t("assessment_response_clear_filter")}
              </Button>
            </Group>
          )}
        </Group>
      )}
      <TextInput
        size="sm"
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        placeholder={t("assessment_response_search")}
      />
      {!hasTree ? (
        <Text size="sm" c="dimmed">
          {t("no_assessment_responses")}
        </Text>
      ) : (
        <Accordion
          multiple
          value={openRoots}
          onChange={setOpenRoots}
          variant="separated"
        >
          {tree.roots.map((root) => (
            <Accordion.Item key={root.concept} value={root.concept}>
              <Accordion.Control>
                <GroupControl
                  label={root.label}
                  countLabel={t("assessment_response_facet_count", {
                    count: root.conceptualItems.length,
                  })}
                />
              </Accordion.Control>
              <Accordion.Panel>
                <Accordion
                  multiple
                  variant="contained"
                  value={root.conceptualItems
                    .map((item) => item.id)
                    .filter((id) => openItems.includes(id))}
                  onChange={(ids) => {
                    const idsInRoot = new Set(
                      root.conceptualItems.map((item) => item.id)
                    );
                    setOpenItems((current) => [
                      ...current.filter((id) => !idsInRoot.has(id)),
                      ...ids,
                    ]);
                  }}
                >
                  {root.conceptualItems.map((item) => (
                    <Accordion.Item key={item.id} value={item.id}>
                      <Accordion.Control>
                        <GroupControl
                          label={item.label}
                          countLabel={t("assessment_response_question_count", {
                            count: item.rows.length,
                          })}
                        />
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="xs">
                          {item.rows.map((row) => (
                            <QuestionRow
                              key={row.instrument_item_id}
                              row={row}
                              t={t}
                            />
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
          {tree.unmapped.length > 0 && (
            <Accordion.Item value="unmapped">
              <Accordion.Control>
                <GroupControl
                  label={t("assessment_response_unmapped")}
                  countLabel={t("assessment_response_question_count", {
                    count: tree.unmapped.length,
                  })}
                />
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {tree.unmapped.map((row) => (
                    <QuestionRow
                      key={row.instrument_item_id}
                      row={row}
                      t={t}
                    />
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>
      )}
    </Stack>
  );
}
